/**
 * Hook for managing normalized weights that always sum to 1.0 (100%).
 *
 * When one weight changes, others are redistributed proportionally
 * to maintain the sum constraint.
 */

import { useCallback } from 'react';

// Use a simple constraint that only requires weight property
// This allows any object with a weight property to be used
export interface WeightedItem {
  weight: number;
}

export interface UseNormalizedWeightsOptions {
  /** Minimum weight for any item (default: 0) */
  minWeight?: number;
  /** Maximum weight for any item (default: 1) */
  maxWeight?: number;
  /** Precision for rounding (decimal places, default: 2) */
  precision?: number;
}

/**
 * Normalize an array of weights to sum to 1.0
 */
export function normalizeWeights<T extends WeightedItem>(
  items: T[],
  options: UseNormalizedWeightsOptions = {}
): T[] {
  const { precision = 2 } = options;

  if (items.length === 0) return [];
  if (items.length === 1) {
    return [{ ...items[0], weight: 1.0 }];
  }

  const total = items.reduce((sum, item) => sum + (item.weight || 0), 0);

  if (total === 0) {
    // Distribute equally if all weights are 0
    const equalWeight = round(1 / items.length, precision);
    const normalized = items.map((item, i) => ({
      ...item,
      weight: i === items.length - 1
        ? round(1 - equalWeight * (items.length - 1), precision) // Last one gets remainder
        : equalWeight
    }));
    return normalized;
  }

  // Normalize proportionally
  const normalized = items.map((item, i) => ({
    ...item,
    weight: round((item.weight || 0) / total, precision)
  }));

  // Adjust last item to ensure exact sum of 1.0
  const currentSum = normalized.slice(0, -1).reduce((sum, item) => sum + item.weight, 0);
  normalized[normalized.length - 1].weight = round(1 - currentSum, precision);

  return normalized;
}

/**
 * Update a single weight and redistribute the difference among other items
 */
export function updateWeight<T extends WeightedItem>(
  items: T[],
  index: number,
  newWeight: number,
  options: UseNormalizedWeightsOptions = {}
): T[] {
  const { minWeight = 0, maxWeight = 1, precision = 2 } = options;

  if (items.length === 0) return [];
  if (items.length === 1) {
    return [{ ...items[0], weight: 1.0 }];
  }

  // Clamp the new weight
  const clampedWeight = Math.max(minWeight, Math.min(maxWeight, newWeight));

  const oldWeight = items[index].weight || 0;
  const delta = clampedWeight - oldWeight;

  if (Math.abs(delta) < 0.001) {
    return items; // No significant change
  }

  // Calculate total weight of other items
  const otherItems = items.filter((_, i) => i !== index);
  const otherTotal = otherItems.reduce((sum, item) => sum + (item.weight || 0), 0);

  const result = items.map((item, i) => {
    if (i === index) {
      return { ...item, weight: round(clampedWeight, precision) };
    }

    if (otherTotal === 0) {
      // Other items have 0 weight, distribute remaining equally
      const remaining = 1 - clampedWeight;
      const equalShare = remaining / (items.length - 1);
      return { ...item, weight: round(equalShare, precision) };
    }

    // Redistribute proportionally
    const proportion = (item.weight || 0) / otherTotal;
    const newItemWeight = (1 - clampedWeight) * proportion;
    return { ...item, weight: round(Math.max(0, newItemWeight), precision) };
  });

  // Ensure sum is exactly 1.0 by adjusting the last non-target item
  const currentSum = result.reduce((sum, item) => sum + item.weight, 0);
  if (Math.abs(currentSum - 1) > 0.001) {
    // Find last item that isn't the one we just changed
    for (let i = result.length - 1; i >= 0; i--) {
      if (i !== index) {
        result[i].weight = round(result[i].weight + (1 - currentSum), precision);
        break;
      }
    }
  }

  return result;
}

/**
 * Add a new item and redistribute weights
 */
export function addItem<T extends WeightedItem>(
  items: T[],
  newItem: T,
  options: UseNormalizedWeightsOptions = {}
): T[] {
  const { precision = 2 } = options;

  if (items.length === 0) {
    return [{ ...newItem, weight: 1.0 }];
  }

  // New item takes equal share from existing items
  const newItemWeight = round(1 / (items.length + 1), precision);
  const scaleFactor = (1 - newItemWeight);

  const scaledItems = items.map(item => ({
    ...item,
    weight: round((item.weight || 0) * scaleFactor, precision)
  }));

  const withNew = [...scaledItems, { ...newItem, weight: newItemWeight }];

  // Ensure sum is exactly 1.0
  return normalizeWeights(withNew, options);
}

/**
 * Remove an item and redistribute its weight to remaining items
 */
export function removeItem<T extends WeightedItem>(
  items: T[],
  index: number,
  options: UseNormalizedWeightsOptions = {}
): T[] {
  if (items.length <= 1) {
    return [];
  }

  const removedWeight = items[index].weight || 0;
  const remaining = items.filter((_, i) => i !== index);
  const remainingTotal = remaining.reduce((sum, item) => sum + (item.weight || 0), 0);

  if (remainingTotal === 0) {
    // Distribute equally
    return normalizeWeights(remaining, options);
  }

  // Redistribute removed weight proportionally
  const result = remaining.map(item => ({
    ...item,
    weight: (item.weight || 0) + removedWeight * ((item.weight || 0) / remainingTotal)
  }));

  return normalizeWeights(result, options);
}

/**
 * Round to specified precision
 */
function round(value: number, precision: number): number {
  const factor = Math.pow(10, precision);
  return Math.round(value * factor) / factor;
}

/**
 * Hook that provides normalized weight management functions
 */
export function useNormalizedWeights<T extends WeightedItem>(
  options: UseNormalizedWeightsOptions = {}
) {
  const normalize = useCallback(
    (items: T[]) => normalizeWeights(items, options),
    [options]
  );

  const update = useCallback(
    (items: T[], index: number, newWeight: number) =>
      updateWeight(items, index, newWeight, options),
    [options]
  );

  const add = useCallback(
    (items: T[], newItem: T) => addItem(items, newItem, options),
    [options]
  );

  const remove = useCallback(
    (items: T[], index: number) => removeItem(items, index, options),
    [options]
  );

  return {
    normalizeWeights: normalize,
    updateWeight: update,
    addItem: add,
    removeItem: remove,
  };
}
